/**
 * WHAT THE HUB'S LOGIN BOX WEARS FOR EACH APP that sends a person to it (operator, 2026-09-22: "when we open the login
 * from whatever app, the ifederated shows the name of the app, and its logo, kinda like it was before"). Every app used
 * to draw its own box in its own words; the box is the hub's now, so the words live HERE, keyed by the app the person
 * came from, and the hub reads them off the return address it was asked to send them back to
 * (`registeredAppIdentity`, `const/registered-app-hosts.ts`; `askingApp`, `apps/ifederated/src/lib/asking-app.ts`).
 *
 * ONE ROW PER REGISTERED BRAND HOST, AND THE HOST LIST IS DERIVED FROM THESE ROWS: `REGISTERED_APP_HOSTS`
 * (`const/registered-app-hosts.ts`) builds its brand hosts off `SSO_BRAND_APPS`' labels, so an app cannot be
 * registered without saying what its box says, nor say what its box
 * says without being registered. The LINE under each heading is the product's own copy leaf (`*_APP_COPY.purpose`, or
 * the brand app's subtitle value); this leaf only pairs it with a host label, so a product renaming its line changes
 * it here too.
 *
 * THE LOCKUP AND THE HEADING, one decision: the app's name appears on the card ONCE. A brand app wears the brand's mark
 * beside the BRAND's name, so the app says itself in its OWN heading -- the apex app still greets with "Welcome home"
 * and the admin app with "Mission Control", exactly as their own boxes did. A product with a name of its own (Noor,
 * Kenecting, the hub itself) wears that name as a wordmark, since none has a mark of its own today
 * (`components/brand/Logo.tsx`), so its heading is the act alone (`LABEL_LOG_IN_OR_SIGN_UP`): a heading naming the
 * product again, straight under its wordmark, said the name twice.
 */

import { IFEDERATED_APP_COPY, IFEDERATED_PRODUCT_NAME } from './ifederated-identity.ts';
import { HUB_LEGAL_OWNER } from './legal-owners.ts';
import { KENECTING_APP_COPY, KENECTING_PRODUCT_NAME } from './kenecting-identity.ts';
import { FEDERATED_APP_COPY, FEDERATED_PRODUCT_NAME } from './federated-identity.ts';
import { LABEL_LOG_IN_OR_SIGN_UP } from './label-values.ts';
import { NOOR_APP_COPY } from './noor-app-copy.ts';
import { NOOR_PRODUCT_NAME } from './noor-identity.ts';
import { PROFILE_COPY } from './profile-shell-copy.ts';
import { TITLE_MISSION_CONTROL } from './apps-admin-federa-title-values.ts';
import {
	SUBTITLE_ADMIN_ACCESS_REQUIRED,
	SUBTITLE_SIGN_IN_TO_CONTINUE,
	SUBTITLE_SIGN_IN_TO_VIEW_ANALYTICS,
	SUBTITLE_SIGN_IN_TO_VIEW_MARKET_MAKER,
} from './subtitle-values.ts';
import { TITLE_WELCOME_HOME } from './title-values.ts';
import type { LegalOwner, SsoAppIdentity, SsoBrandApp } from '@federa/types/single-sign-on';

/** A brand app: the brand's mark and name above the app's own heading, under the brand's one set of terms. */
function brandApp(title: string, subtitle: string): SsoAppIdentity {
	return { lockup: { kind: 'brand-mark' }, title, subtitle, accent: 'brand', legalOwner: 'brand' };
}

/** A product with a name of its own: that name as a wordmark, over the one act every such box heads with and the
 *  product's own line, under the product's own terms (`const/legal-owners.ts`). */
function ownNameApp(name: string, copy: { readonly purpose: string }, legalOwner: LegalOwner): SsoAppIdentity {
	return { lockup: { kind: 'wordmark', text: name }, title: LABEL_LOG_IN_OR_SIGN_UP, subtitle: copy.purpose, accent: 'ink', legalOwner };
}

/** The app that owns the brand apex (and `www` in production): the greeting its own box gave. */
export const SSO_BRAND_APEX_APP: SsoAppIdentity = brandApp(TITLE_WELCOME_HOME, SUBTITLE_SIGN_IN_TO_CONTINUE);

/** The brand's apps under its apex, in the order the host list publishes them. */
export const SSO_BRAND_APPS: readonly SsoBrandApp[] = [
	{ label: null, identity: SSO_BRAND_APEX_APP },
	{ label: 'admin', identity: brandApp(TITLE_MISSION_CONTROL, SUBTITLE_ADMIN_ACCESS_REQUIRED) },
	{ label: 'analytics', identity: brandApp(TITLE_WELCOME_HOME, SUBTITLE_SIGN_IN_TO_VIEW_ANALYTICS) },
	// The standalone explore host serves the same app as the apex: the same box.
	{ label: 'explore', identity: SSO_BRAND_APEX_APP },
	{ label: 'market-maker', identity: brandApp(TITLE_WELCOME_HOME, SUBTITLE_SIGN_IN_TO_VIEW_MARKET_MAKER) },
	{ label: 'profile', identity: brandApp(TITLE_WELCOME_HOME, PROFILE_COPY.purpose) },
];

/** Noor on its own apex (`const/noor-identity.ts`; operator, 2026-09-26: "the dev should be dev.noor.partners"). */
export const NOOR_SSO_APP: SsoAppIdentity = ownNameApp(NOOR_PRODUCT_NAME, NOOR_APP_COPY, 'noor');

/** The desk on its own apex (`const/kenecting-identity.ts`). */
export const KENECTING_SSO_APP: SsoAppIdentity = ownNameApp(KENECTING_PRODUCT_NAME, KENECTING_APP_COPY, 'kenecting');

/** Federated Holdings on its own apex (`const/federated-identity.ts`). */
export const FEDERATED_SSO_APP: SsoAppIdentity = ownNameApp(FEDERATED_PRODUCT_NAME, FEDERATED_APP_COPY, 'federated');

/** The hub itself: what its box wears when the person opened the hub rather than arriving from an app. Spelled beside
 *  the brand's name on purpose: the hub is not a brand app, and its box must never read as one. Its terms are the
 *  account's own ({@link HUB_LEGAL_OWNER}), so its box names no second owner. */
export const IFEDERATED_SSO_APP: SsoAppIdentity = ownNameApp(IFEDERATED_PRODUCT_NAME, IFEDERATED_APP_COPY, HUB_LEGAL_OWNER);
